import { QueryKey } from '@tanstack/react-query';
import queryClient from './query-client';
import { useQueryWithCallback } from './use-query-with-callback';

export { mutationScopes } from './mutation-scopes';
export { queryKeys } from './query-keys';

export {
    keepPreviousData,
    QueryClientProvider,
    skipToken,
    useMutation,
    useQueries,
    useQuery,
    useQueryClient,
} from '@tanstack/react-query';
export type {
    MutateOptions,
    QueryKey,
    UseQueryResult,
} from '@tanstack/react-query';

export const invalidateQueryKey = (key: QueryKey) => {
    return queryClient.invalidateQueries({ queryKey: key });
};

export interface ItemToQueryMapping<T> {
    item: T;
    queryKey: QueryKey;
}
interface CachedQuery<ItemType, DataType> {
    // Preserved from the input ItemToQueryMapping so callers that build
    // keyed maps (e.g. Record<string, T>) can identify each entry without
    // having to reach into queryKey to recover the original item value.
    item: ItemType;
    queryKey: QueryKey;
    data: DataType;
}

interface QueryCacheCheckResult<ItemType, DataType> {
    cachedQueries: CachedQuery<ItemType, DataType>[];
    invalidQueries: ItemToQueryMapping<ItemType>[];
}

/**
 * Checks the query cache for existing data and determines which queries need fetching
 *
 * This function examines each provided query key to determine if its corresponding data
 * exists in the cache and is still fresh (not stale). It categorizes queries into two groups:
 * - cachedQueries: Queries that have valid, non-stale data in the cache
 * - invalidQueries: Queries that either don't exist in the cache or have stale data
 *
 * This allows components to optimize data fetching by only requesting data that isn't
 * already available in the cache, while still using cached data that's still fresh.
 *
 * @template ItemType - The type of item being mapped to a query key (e.g., string for pack classes)
 * @template DataType - The expected data type returned by the query (e.g., MediaPackType)
 *
 * @param keyObjects - An array of objects mapping items to their query keys
 * @param staleTime - The duration in milliseconds before cached data is considered stale
 *
 * @returns An object containing both cached queries (with their data) and invalid queries
 * that need to be fetched
 */
export function checkQueryCache<ItemType, DataType>(
    keyObjects: ItemToQueryMapping<ItemType>[],
    staleTime: number
): QueryCacheCheckResult<ItemType, DataType> {
    return keyObjects.reduce<QueryCacheCheckResult<ItemType, DataType>>(
        ({ cachedQueries, invalidQueries }, keyObj) => {
            const { queryKey } = keyObj;
            const queryState = queryClient.getQueryState<DataType>(queryKey);
            const cachedItem = queryState?.data;

            // If query exists and is not stale, use the cached data
            if (
                cachedItem &&
                queryState &&
                !queryState.isInvalidated &&
                queryState.dataUpdatedAt &&
                queryState.dataUpdatedAt > Date.now() - staleTime
            ) {
                cachedQueries.push({
                    item: keyObj.item,
                    queryKey,
                    data: cachedItem,
                });
            } else {
                invalidQueries.push(keyObj);
            }
            return { cachedQueries, invalidQueries };
        },
        { cachedQueries: [], invalidQueries: [] }
    );
}

export { queryClient, useQueryWithCallback };
